/**
 * RBAC Dashboard Component
 * Epic #17: RBAC & Permissions Platform Services
 *
 * Comprehensive RBAC management dashboard for platform administrators
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Column,
  Tile,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  Button,
  SkeletonText,
  InlineNotification,
  ProgressBar,
  Loading,
} from '@carbon/react';
import {
  UserRole,
  Security,
  ChartLine,
  Report,
  Locked,
  Unlocked,
  TreeView,
  Checkmark,
  DocumentPdf,
} from '@carbon/icons-react';
import { DonutChart } from '@carbon/charts-react';
import '@carbon/charts-react/styles.css';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';
import type { DashboardMetrics, RoleHierarchyResponse } from '../../../../shared/interfaces/rbac.interface';
import PermissionTestingModal from '../../components/rbac/PermissionTestingModal';
import styles from './RBACDashboard.module.scss';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface RBACDashboardProps {}

const RBACDashboard: React.FC<RBACDashboardProps> = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Get token from localStorage directly - memoized to prevent infinite re-renders
  const [token] = useState(() => localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [roleHierarchy, setRoleHierarchy] = useState<RoleHierarchyResponse | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [permissionTestModalOpen, setPermissionTestModalOpen] = useState(false);

  // Initial data load
  useEffect(() => {
    let mounted = true;

    const fetchDashboardMetrics = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/v1/admin/rbac/dashboard-metrics`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (mounted && response.data.success) {
          setMetrics(response.data.data);
          setError(null);
        } else if (mounted) {
          setError(response.data.message || 'Failed to load dashboard metrics');
        }
      } catch (err: any) {
        console.error('Failed to fetch dashboard metrics:', err);
        if (mounted) {
          setError(err.response?.data?.message || err.message || 'Failed to load dashboard metrics');
        }
      }
    };

    const fetchRoleHierarchy = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/v1/admin/rbac/role-hierarchy`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (mounted && response.data.success) {
          setRoleHierarchy(response.data.data);
        }
      } catch (err: any) {
        console.error('Failed to fetch role hierarchy:', err);
        // Don't show error for hierarchy as it's secondary data
      }
    };

    const loadData = async () => {
      if (!token) {
        if (mounted) {
          setLoading(false);
          setError('Authentication required');
        }
        return;
      }

      if (mounted) setLoading(true);
      try {
        await Promise.all([fetchDashboardMetrics(), fetchRoleHierarchy()]);
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        // Error already set by individual fetch functions
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [token]);

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);

    try {
      const [metricsResponse, hierarchyResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/v1/admin/rbac/dashboard-metrics`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/v1/admin/rbac/role-hierarchy`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (metricsResponse.data.success) {
        setMetrics(metricsResponse.data.data);
        setError(null);
      }
      if (hierarchyResponse.data.success) {
        setRoleHierarchy(hierarchyResponse.data.data);
      }
    } catch (err: any) {
      console.error('Failed to refresh data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  // Format timestamp for display
  const formatTimestamp = React.useCallback((timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  }, []);

  // Table headers for recent events (memoized to prevent re-creation)
  const recentEventsHeaders = React.useMemo(
    () => [
      { key: 'user_email', header: 'User' },
      { key: 'action', header: 'Action' },
      { key: 'resource_type', header: 'Resource' },
      { key: 'result', header: 'Result' },
      { key: 'timestamp', header: 'Time' },
    ],
    []
  );

  // Prepare donut chart data (memoized)
  const chartData = React.useMemo(() => {
    if (!metrics) return [];

    return metrics.role_distribution.map(role => ({
      group: role.role_name,
      value: role.user_count,
    }));
  }, [metrics]);

  // Prepare table rows for recent events (memoized)
  const recentEventsRows = React.useMemo(() => {
    if (!metrics) return [];

    return metrics.recent_events.map((event, index) => ({
      id: `${index}`,
      user_email: event.user_email,
      action: event.action,
      resource_type: event.resource_type,
      result: event.result,
      timestamp: formatTimestamp(event.timestamp),
    }));
  }, [metrics, formatTimestamp]);

  // Donut chart options (memoized to prevent re-render loop)
  const chartOptions = React.useMemo(() => {
    if (!metrics) return {};

    return {
      title: '',
      resizable: true,
      height: '300px',
      donut: {
        center: {
          label: 'Total Users',
          number: metrics.total_users,
        },
      },
      legend: {
        alignment: 'center',
      },
    };
  }, [metrics]);

  if (loading) {
    return (
      <div className="rbac-dashboard-loading">
        <Loading description="Loading RBAC Dashboard..." withOverlay={false} />
      </div>
    );
  }

  if (error) {
    return (
      <Grid fullWidth>
        <Column lg={16}>
          <InlineNotification
            kind="error"
            title="Error Loading Dashboard"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
          />
        </Column>
      </Grid>
    );
  }

  if (!metrics) {
    return (
      <Grid fullWidth>
        <Column lg={16}>
          <InlineNotification
            kind="warning"
            title="No Data Available"
            subtitle="Dashboard metrics could not be loaded"
          />
        </Column>
      </Grid>
    );
  }

  return (
    <Grid fullWidth className={styles.rbacDashboard}>
      {/* Header Section */}
      <Column lg={16}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>RBAC & Permissions Dashboard</h1>
          <p className={styles.pageDescription}>Manage roles, permissions, and access control across the platform</p>
        </div>
      </Column>

      {/* Key Metrics - 4 Tiles */}
      <Column lg={4} md={4} sm={4}>
        <Tile className={styles.metricTile}>
          <div className={styles.metricIconContainer}>
            <UserRole size={32} />
          </div>
          <h3 className={styles.metricValue}>{metrics.total_users}</h3>
          <p className={styles.metricLabel}>Active Users</p>
          <Tag type="green" size="sm">
            Active
          </Tag>
        </Tile>
      </Column>

      <Column lg={4} md={4} sm={4}>
        <Tile className={styles.metricTile}>
          <div className={styles.metricIconContainer}>
            <Security size={32} />
          </div>
          <h3 className={styles.metricValue}>{roleHierarchy?.total_roles || 7}</h3>
          <p className={styles.metricLabel}>Role Types</p>
          <Tag type="blue" size="sm">
            All Active
          </Tag>
        </Tile>
      </Column>

      <Column lg={4} md={4} sm={4}>
        <Tile className={styles.metricTile}>
          <div className={styles.metricIconContainer}>
            <ChartLine size={32} />
          </div>
          <h3 className={styles.metricValue}>{metrics.permission_checks_24h.toLocaleString()}</h3>
          <p className={styles.metricLabel}>Permission Checks (24h)</p>
          <Tag type="purple" size="sm">
            Avg {metrics.avg_evaluation_time_ms.toFixed(1)}ms
          </Tag>
        </Tile>
      </Column>

      <Column lg={4} md={4} sm={4}>
        <Tile className={styles.metricTile}>
          <div className={styles.metricIconContainer}>
            <Report size={32} />
          </div>
          <h3 className={styles.metricValue}>{metrics.access_denials_24h.toLocaleString()}</h3>
          <p className={styles.metricLabel}>Access Denials (24h)</p>
          <Tag type={metrics.denial_rate < 5 ? 'green' : metrics.denial_rate < 10 ? 'purple' : 'red'} size="sm">
            {metrics.denial_rate.toFixed(2)}%
          </Tag>
        </Tile>
      </Column>

      {/* Quick Actions Section */}
      <Column lg={16}>
        <Tile className={styles.actionTile}>
          <h3 className={styles.sectionTitle}>Quick Actions</h3>
          <div className={styles.actionButtons}>
            <Button kind="primary" renderIcon={UserRole} onClick={() => navigate('/admin/rbac/assign-role')}>
              Assign Role
            </Button>
            <Button kind="secondary" renderIcon={Checkmark} onClick={() => setPermissionTestModalOpen(true)}>
              Test Permissions
            </Button>
            <Button kind="secondary" renderIcon={Security} onClick={() => navigate('/admin/rbac/roles')}>
              Manage Roles
            </Button>
            <Button kind="secondary" renderIcon={TreeView} onClick={() => navigate('/admin/rbac/hierarchy')}>
              View Role Hierarchy
            </Button>
            <Button kind="tertiary" renderIcon={Report} onClick={() => navigate('/admin/rbac/audit')}>
              View Audit Logs
            </Button>
            <Button kind="tertiary" renderIcon={DocumentPdf} onClick={() => navigate('/admin/rbac/compliance')}>
              Compliance Reports
            </Button>
            <Button kind="ghost" onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </Button>
          </div>
        </Tile>
      </Column>

      {/* Permission Testing Modal */}
      <PermissionTestingModal open={permissionTestModalOpen} onClose={() => setPermissionTestModalOpen(false)} />

      {/* Role Distribution Chart */}
      <Column lg={8} md={8} sm={4}>
        <Tile className={styles.chartTile}>
          <h3 className={styles.sectionTitle}>Role Distribution</h3>
          <div className={styles.chartContainer}>
            <DonutChart data={chartData} options={chartOptions} />
          </div>
        </Tile>
      </Column>

      {/* Recent Activity */}
      <Column lg={8} md={8} sm={4}>
        <Tile className={styles.tableTile}>
          <h3 className={styles.sectionTitle}>Recent Access Events</h3>
          <DataTable rows={recentEventsRows} headers={recentEventsHeaders} size="sm">
            {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
              <TableContainer>
                <Table {...getTableProps()}>
                  <TableHead>
                    <TableRow>
                      {headers.map(header => (
                        <TableHeader {...getHeaderProps({ header })} key={header.key}>
                          {header.header}
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(row => (
                      <TableRow {...getRowProps({ row })} key={row.id}>
                        {row.cells.map(cell => (
                          <TableCell key={cell.id}>
                            {cell.info.header === 'result' ? (
                              <Tag type={cell.value === 'granted' ? 'green' : 'red'} size="sm">
                                {cell.value}
                              </Tag>
                            ) : (
                              cell.value
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DataTable>
        </Tile>
      </Column>

      {/* Compliance Status */}
      <Column lg={16}>
        <Tile className={styles.complianceTile}>
          <h3 className={styles.sectionTitle}>Compliance Status</h3>
          <Grid>
            <Column lg={5} md={8} sm={4}>
              <div className={styles.complianceItem}>
                <h4 className={styles.complianceTitle}>FERPA Compliance</h4>
                <ProgressBar
                  value={metrics.compliance.ferpa_compliance}
                  label={`${metrics.compliance.ferpa_compliance.toFixed(1)}% Compliant`}
                  helperText="Educational Records Privacy"
                />
              </div>
            </Column>
            <Column lg={5} md={8} sm={4}>
              <div className={styles.complianceItem}>
                <h4 className={styles.complianceTitle}>COPPA Compliance</h4>
                <ProgressBar
                  value={metrics.compliance.coppa_compliance}
                  label={`${metrics.compliance.coppa_compliance.toFixed(1)}% Compliant`}
                  helperText="Children's Online Privacy"
                />
              </div>
            </Column>
            <Column lg={6} md={8} sm={4}>
              <div className={styles.complianceItem}>
                <h4 className={styles.complianceTitle}>Access Control Health</h4>
                <ProgressBar
                  value={metrics.compliance.access_control_health}
                  label={`${metrics.compliance.access_control_health.toFixed(1)}% Healthy`}
                  helperText="Permission System Status"
                />
              </div>
            </Column>
          </Grid>
        </Tile>
      </Column>

      {/* Cache Performance */}
      <Column lg={16}>
        <Tile className={styles.cacheTile}>
          <h3 className={styles.sectionTitle}>Cache Performance</h3>
          <div className={styles.cacheMetrics}>
            <div className={styles.cacheMetricItem}>
              <p className={styles.cacheMetricLabel}>Hit Rate</p>
              <p className={styles.cacheMetricValue}>{metrics.cache_performance.hit_rate.toFixed(1)}%</p>
            </div>
            <div className={styles.cacheMetricItem}>
              <p className={styles.cacheMetricLabel}>Avg Cache Hit Time</p>
              <p className={styles.cacheMetricValue}>{metrics.cache_performance.avg_cache_hit_time_ms.toFixed(1)}ms</p>
            </div>
            <div className={styles.cacheTimestamp}>
              <p className={styles.timestampText}>Last Updated: {formatTimestamp(metrics.last_updated)}</p>
            </div>
          </div>
        </Tile>
      </Column>
    </Grid>
  );
};

export default RBACDashboard;
